import React from "react";
import {
  Button,
  Collapse,
  ConfigProvider,
  Dropdown,
  Space,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import type { MenuProps } from "antd";
import {
  CheckCircleOutlined,
  DownOutlined,
  EditOutlined,
  FileSearchOutlined,
  InboxOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";
import { etiquetaEstado } from "./estados";
import type { ProductState } from "./types";

/**
 * El último paso cambia de nombre según cómo terminó: Pausado y Archivado no son pasos del
 * camino, son los finales posibles después de Activo (xcons-docs#404). Los nombres salen
 * de `estados.ts`, los mismos de la columna Estado del listado.
 */
const FINAL: Record<"active" | "inactive" | "archived", React.ReactNode> = {
  active: <CheckCircleOutlined />,
  inactive: <PauseCircleOutlined />,
  archived: <InboxOutlined />,
};

const { Text } = Typography;

/** El hover de las opciones de Acciones: el verde claro de la paleta (`xcons.1`). */
export const TEMA_MENU = {
  components: { Dropdown: { controlItemBgHover: "#e9f4ef" } },
};

/** El mismo encabezado de 40px que las secciones de la ficha (`FichaSoloLectura`). */
const TEMA_SECCION = {
  components: { Collapse: { headerPadding: "9px 16px" } },
};

/**
 * **El encabezado de la sección toma el color del estado** (xcons-docs#479): su propio
 * borde, en el tono más oscuro, y el fondo en el mismo color más suave. El contenido queda
 * blanco y el borde de la sección, el de las demás. Los colores viven en `config.css`
 * (`.estado-seccion--*`). Borrador no lleva clase: queda en el gris de las demás secciones.
 * Reemplaza al bullet de Pausado y Archivado.
 */
const CON_COLOR: ProductState[] = ["active", "review", "inactive", "archived"];
const claseDeSeccion = (state: ProductState) =>
  CON_COLOR.includes(state) ? `estado-seccion estado-seccion--${state}` : undefined;

const INDICE: Record<ProductState, number> = {
  draft: 0,
  review: 1,
  active: 2,
  inactive: 2,
  archived: 2,
};

/** Una acción del menú ▾: cambia el estado del producto. */
export interface AccionDeEstado {
  key: string;
  label: string;
  onClick: () => void;
  /**
   * **Deshabilitada y explicada, no escondida** (R15): un Editor tiene que ver que existe
   * "Publicar" y por qué no le toca a él. El motivo va escrito debajo de la opción.
   */
  disabled?: boolean;
  motivo?: string;
  danger?: boolean;
  /** Va separada de las anteriores por una línea: Editar, que no cambia el estado. */
  separada?: boolean;
  /**
   * Se ve deshabilitada pero **responde al click**, y el click corre esto: Enviar a
   * revisión con obligatorios sin cargar los marca en rojo. Un botón `disabled` de verdad
   * no registra el click, así que no habría cómo mostrar qué falta.
   */
  alBloqueado?: () => void;
}

interface Props {
  state: ProductState;
  /**
   * El paso que marca el flujo, si no es el del estado del producto. Una versión en revisión
   * de un producto activo se marca en **En revisión**: Acciones va sobre la línea hacia
   * Activo, y Activo queda gris porque lo nuevo todavía no se aprobó (Diseño, 2026-10-01).
   * El tag del encabezado sigue diciendo el estado del producto.
   */
  paso?: ProductState;
  /**
   * Lo que se puede hacer desde el estado actual, en el menú del botón Acciones: sobre la
   * línea hacia el estado siguiente o, si el producto ya está publicado, debajo del último.
   * Sin acciones no hay botón y el flujo sólo muestra.
   */
  acciones?: AccionDeEstado[];
  /** Ocupa todo el ancho, en su propia fila debajo del nombre, en vez de ir a la derecha. */
  anchoCompleto?: boolean;
  /** Cuándo se creó y se editó por última vez: va en el encabezado de la sección. */
  fechas?: { creado?: string | null; editado?: string | null };
  /**
   * Etiquetas de contexto que van junto al título, después del estado: Nunca publicado,
   * Versión de edición.
   */
  etiquetas?: React.ReactNode;
}

/**
 * Flujo de estado del producto. Los cambios de estado se eligen desde el botón
 * **Acciones**, que va donde está la decisión (xcons-docs#404):
 *
 * - Mientras el producto avanza —Borrador, En revisión—, **sobre la línea entre el estado
 *   actual y el siguiente**. El siguiente se ve deshabilitado: todavía no se llegó.
 * - Cuando ya está publicado —Activo, Pausado, Archivado— no hay siguiente, y el botón va
 *   **debajo del último estado**, con las opciones de un producto activo.
 *
 * Los gates los resuelve quien pasa las acciones. Los tamaños son los del flujo de
 * entregas de Pedidos (`components/order-shipment/steps.tsx`), no los de Presupuestos.
 */
export const ProductStateSteps: React.FC<Props> = ({
  state,
  paso: pasoMarcado,
  acciones = [],
  anchoCompleto = false,
  fechas,
  etiquetas,
}) => {
  const actual = INDICE[pasoMarcado ?? state];
  const alFinal = state === "draft" || state === "review" ? "active" : state;
  const publicado = actual === 2;

  const pasos = [
    { key: "draft", label: etiquetaEstado("draft"), icon: <EditOutlined /> },
    {
      key: "review",
      label: etiquetaEstado("review"),
      icon: <FileSearchOutlined />,
    },
    { key: "final", label: etiquetaEstado(alFinal), icon: FINAL[alFinal] },
  ];

  /**
   * **Enviar a revisión va a la vista, como botón con su nombre** (xcons-docs#442): es el
   * paso siguiente de un borrador y escondido en el menú ▾ no se encontraba. En borrador es
   * lo único: sin Descartar ni Editar (Diseño, 2026-09-30).
   */
  const enviar = acciones.find((a) => a.key === "review");
  const resto = acciones.filter((a) => a !== enviar);

  const menu: MenuProps = {
    items: resto.flatMap((a) => [
      ...(a.separada
        ? [{ type: "divider" as const, key: `${a.key}-sep` }]
        : []),
      {
        key: a.key,
        danger: a.danger,
        disabled: a.disabled,
        // El motivo va escrito debajo y no en un tooltip: antd no dispara el hover sobre un
        // ítem deshabilitado, así que el tooltip nunca aparecía.
        label:
          a.disabled && a.motivo ? (
            <div style={{ maxWidth: 280 }}>
              <div>{a.label}</div>
              <div style={{ fontSize: 12, whiteSpace: "normal" }}>
                {a.motivo}
              </div>
            </div>
          ) : (
            a.label
          ),
        onClick: a.disabled ? undefined : a.onClick,
      },
    ]),
  };

  // El menú dice **Acciones** (Diseño, 2026-09-30): en revisión es Aprobar y Rechazar, y una
  // flecha sola no decía qué había adentro. Es el mismo botón que Enviar a revisión, con la
  // flecha para desplegar (Diseño, 2026-10-01).
  // Las opciones en negro, ninguna en rojo, y el hover en el verde claro (xcons-docs#479).
  const botonMenu = resto.length ? (
    <ConfigProvider theme={TEMA_MENU}>
      <Dropdown menu={menu} trigger={["click"]}>
        <Button>
          Acciones <DownOutlined />
        </Button>
      </Dropdown>
    </ConfigProvider>
  ) : null;

  // Deshabilitado con `alBloqueado`: se ve apagado pero el click llega. Sin él, deshabilitado
  // de verdad, y el `span` es para que el tooltip aparezca igual sobre el botón apagado.
  const botonEnviar = !enviar ? null : enviar.disabled && enviar.alBloqueado ? (
    <Tooltip title={enviar.motivo}>
      <Button
        className="boton-como-deshabilitado"
        aria-disabled
        onClick={enviar.alBloqueado}
      >
        {enviar.label}
      </Button>
    </Tooltip>
  ) : enviar.disabled ? (
    <Tooltip title={enviar.motivo}>
      <span>
        <Button disabled>{enviar.label}</Button>
      </span>
    </Tooltip>
  ) : (
    <Button onClick={enviar.onClick}>{enviar.label}</Button>
  );

  const botonAcciones =
    botonEnviar || botonMenu ? (
      <Space size={8}>
        {botonEnviar}
        {botonMenu}
      </Space>
    ) : null;

  // Sin etiqueta de "Cambios en revisión" o "en borrador" (xcons-docs#479): que lo publicado
  // tiene una versión de edición lo dice el link que la ficha pasa en `etiquetas`.

  const flujo = (
    <div className="flujo-estado">
      {pasos.map((paso, i) => (
        <React.Fragment key={paso.key}>
          <div
            className={[
              "flujo-estado__paso",
              i < actual && "flujo-estado__paso--hecho",
              i === actual && "flujo-estado__paso--actual",
              i === actual && publicado && `flujo-estado__paso--${state}`,
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className="flujo-estado__circulo">{paso.icon}</div>
            <div className="flujo-estado__nombre">{paso.label}</div>
            {/* Publicado: no hay estado siguiente, las acciones van debajo del último. */}
            {publicado && i === 2 && botonAcciones}
          </div>
          {i < pasos.length - 1 && (
            <div
              className={`flujo-estado__conector${
                i < actual ? " flujo-estado__conector--hecho" : ""
              }`}
            >
              <div className="flujo-estado__linea" />
              {/* Todavía no publicado: las acciones van entre el actual y el siguiente. */}
              {!publicado && i === actual && botonAcciones && (
                <>
                  {botonAcciones}
                  <div className="flujo-estado__linea" />
                </>
              )}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  if (!anchoCompleto) return flujo;

  const formato = (fecha?: string | null) =>
    fecha ? dayjs(fecha).format("DD/MM/YYYY HH:mm") : null;
  const creado = formato(fechas?.creado);
  const editado = formato(fechas?.editado);

  // A todo el ancho va como una sección más de la ficha: **el mismo encabezado gris** que
  // Datos principales o Categorías, no una tarjeta con su propio estilo. No se colapsa —
  // esconder el estado y sus acciones no le sirve a nadie—. **Junto al título, el estado**
  // y sus etiquetas de contexto (xcons-docs#442); a la derecha, las fechas. **Sin tag de
  // estado** (xcons-docs#479): lo dicen el flujo y el color de la sección.
  return (
    // El color es el del paso que marca el flujo: una versión de edición en revisión es
    // naranja aunque lo publicado siga Activo.
    <ConfigProvider theme={TEMA_SECCION}>
      <Collapse
        className={claseDeSeccion(pasoMarcado ?? state)}
        activeKey={["estado"]}
        collapsible="icon"
        items={[
          {
            key: "estado",
            showArrow: false,
            label: (
              <Space size={8} wrap>
                <Text strong>Estado del producto</Text>
                {etiquetas}
              </Space>
            ),
            extra: (creado || editado) && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {[
                  creado && `Creado el ${creado}`,
                  editado && `Última edición el ${editado}`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Text>
            ),
            children: flujo,
          },
        ]}
      />
    </ConfigProvider>
  );
};
